import { useState } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod/v3'
import { createFileRoute } from '@tanstack/react-router'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import type { ShopTier, ShopTierInput } from '@/lib/api/shop-tier-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MoneyPreview } from '@/components/ui/money-preview'
import { Switch } from '@/components/ui/switch'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import {
    useDeleteShopTier,
    useSaveShopTier,
    useShopTiers,
} from '@/hooks/use-shop-tier-query'
import { ApiError } from '@/lib/api/client'
import {
    SHOP_TIER_DISCOUNT_MAX,
    SHOP_TIER_MIN_SPEND_MAX,
    SHOP_TIER_NAME_MAX,
} from '@/lib/api/shop-tier-api'
import { resolveApiErrorMessage } from '@/lib/api-error-messages'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { requireMemberTiersEnabled } from '@/lib/shop-module'

// Hạng thành viên bán lẻ (web + Zalo Mini App). Thứ tự hạng = mốc chi tiêu
// tăng dần (mốc không trùng), nên bảng không có kéo thả.
export const Route = createFileRoute(
    '/_app/_authed/_sidebar/retail/member-tiers',
)({
    // Ẩn ở mọi môi trường (ERP-07) — xem RETAIL_MEMBER_TIERS_ENABLED.
    beforeLoad: () => requireMemberTiersEnabled(),
    component: MemberTiersPage,
})

export interface TierForm {
    name: string
    minSpend: string
    discount: string
    freeShipping: boolean
}

export type TierField = 'name' | 'minSpend' | 'discount'
export type TierErrors = Partial<Record<TierField | 'form', string>>

const EMPTY_FORM: TierForm = {
    name: '',
    minSpend: '',
    discount: '0',
    freeShipping: false,
}

/** Kiểm form như server (tên 1–50 ký tự, mốc số nguyên ≥ 0, ưu đãi 0–10 %, tối đa 2 số lẻ). */
export function validateTierForm(f: TierForm): {
    errors: TierErrors
    input?: ShopTierInput
} {
    const errors: TierErrors = {}
    const name = f.name.trim().split(/\s+/).filter(Boolean).join(' ')
    if (!name) errors.name = m.shop_tier_name_required()
    else if ([...name].length > SHOP_TIER_NAME_MAX)
        errors.name = m.shop_tier_name_too_long({
            max: String(SHOP_TIER_NAME_MAX),
        })
    const minSpend = Number(f.minSpend.trim())
    if (
        f.minSpend.trim() === '' ||
        !Number.isInteger(minSpend) ||
        minSpend < 0 ||
        minSpend > SHOP_TIER_MIN_SPEND_MAX
    )
        errors.minSpend = m.shop_tier_min_spend_invalid()
    const discount = f.discount.trim() === '' ? 0 : Number(f.discount)
    if (
        !Number.isFinite(discount) ||
        discount < 0 ||
        discount > SHOP_TIER_DISCOUNT_MAX ||
        Math.abs(discount * 100 - Math.round(discount * 100)) > 1e-9
    )
        errors.discount = m.shop_tier_discount_invalid({
            max: String(SHOP_TIER_DISCOUNT_MAX),
        })
    if (Object.keys(errors).length > 0) return { errors }
    return {
        errors,
        input: {
            name,
            min_spend: minSpend,
            discount_percent: discount,
            free_shipping: f.freeShipping,
        },
    }
}

// Mã lỗi server → ô hiện lỗi; câu tiếng Việt từ api-error-messages.
const SERVER_FIELD: Record<string, TierField> = {
    SHOP_TIER_NAME_INVALID: 'name',
    SHOP_TIER_NAME_DUPLICATE: 'name',
    SHOP_TIER_MIN_SPEND_INVALID: 'minSpend',
    SHOP_TIER_MIN_SPEND_DUPLICATE: 'minSpend',
    SHOP_TIER_DISCOUNT_INVALID: 'discount',
}

export function serverTierErrors(err: unknown): TierErrors {
    if (err instanceof ApiError) {
        const text =
            resolveApiErrorMessage(err.code, err.params) ??
            m.shop_tier_save_failed()
        return { [SERVER_FIELD[err.code] ?? 'form']: text }
    }
    return { form: m.shop_tier_save_failed() }
}

// Cùng luật với validateTierForm, gắn lỗi vào đúng ô của react-hook-form.
const tierSchema = () =>
    z.custom<TierForm>().superRefine((f, ctx) => {
        for (const [path, message] of Object.entries(
            validateTierForm(f).errors,
        )) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message })
        }
    })

export function MemberTiersPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('shop_tiers.write')
    const query = useShopTiers()
    const save = useSaveShopTier()
    const remove = useDeleteShopTier()
    const tiers = query.data?.data ?? []

    const [editing, setEditing] = useState<ShopTier | 'new' | null>(null)
    const form = useForm<TierForm>({
        resolver: zodResolver(tierSchema()),
        defaultValues: EMPTY_FORM,
    })
    const minSpend = useWatch({ control: form.control, name: 'minSpend' })
    const errors = form.formState.errors
    const [deleting, setDeleting] = useState<ShopTier | null>(null)

    const open = (t: ShopTier | 'new') => {
        setEditing(t)
        form.reset(
            t === 'new'
                ? EMPTY_FORM
                : {
                      name: t.name,
                      minSpend: String(t.min_spend),
                      discount: String(t.discount_percent),
                      freeShipping: t.free_shipping,
                  },
        )
    }

    const submit = form.handleSubmit(async (values) => {
        const { input } = validateTierForm(values)
        if (!input || editing === null) return
        try {
            await save.mutateAsync({
                id: editing === 'new' ? undefined : editing.id,
                input,
            })
            setEditing(null)
        } catch (err) {
            for (const [field, message] of Object.entries(
                serverTierErrors(err),
            )) {
                form.setError(
                    field === 'form' ? 'root.server' : (field as TierField),
                    { type: 'server', message },
                )
            }
        }
    })

    const confirmDelete = async () => {
        if (!deleting) return
        await remove.mutateAsync(deleting.id).catch(() => undefined)
        setDeleting(null)
    }

    const colCount = canWrite ? 6 : 5

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                title={m.shop_tier_title()}
                subtitle={m.shop_tier_subtitle()}
                primaryActions={
                    canWrite ? (
                        <Button size="sm" onClick={() => open('new')}>
                            <Plus className="mr-1 size-4" />
                            {m.shop_tier_add()}
                        </Button>
                    ) : undefined
                }
            />

            <div className="overflow-x-auto rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.shop_tier_name_label()}</TableHead>
                            <TableHead className="text-right">
                                {m.shop_tier_min_spend_label()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.shop_tier_discount_label()}
                            </TableHead>
                            <TableHead className="text-center">
                                {m.shop_tier_free_shipping_label()}
                            </TableHead>
                            <TableHead className="text-right">
                                {m.shop_tier_account_count_label()}
                            </TableHead>
                            {canWrite && <TableHead className="w-24" />}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {query.isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={colCount}
                                    className="text-muted-foreground"
                                >
                                    {m.common_loading()}
                                </TableCell>
                            </TableRow>
                        ) : query.isError ? (
                            <TableRow>
                                <TableCell
                                    colSpan={colCount}
                                    className="text-destructive"
                                >
                                    {m.shop_tier_load_failed()}
                                </TableCell>
                            </TableRow>
                        ) : tiers.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={colCount}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.shop_tier_empty()}
                                </TableCell>
                            </TableRow>
                        ) : (
                            tiers.map((t) => (
                                <TableRow key={t.id}>
                                    <TableCell className="font-medium">
                                        {t.name}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {m.shop_tier_from({
                                            amount: formatVnd(t.min_spend),
                                        })}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {t.discount_percent > 0
                                            ? `${t.discount_percent}%`
                                            : '—'}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {t.free_shipping
                                            ? m.shop_tier_free_shipping_yes()
                                            : '—'}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {t.account_count ?? '—'}
                                    </TableCell>
                                    {canWrite && (
                                        <TableCell>
                                            <div className="flex justify-end gap-1">
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    aria-label={m.shop_tier_edit_aria(
                                                        { name: t.name },
                                                    )}
                                                    onClick={() => open(t)}
                                                >
                                                    <Pencil className="size-4" />
                                                </Button>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    className="text-destructive"
                                                    aria-label={m.shop_tier_delete_aria(
                                                        { name: t.name },
                                                    )}
                                                    onClick={() =>
                                                        setDeleting(t)
                                                    }
                                                >
                                                    <Trash2 className="size-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
            <p className="text-sm italic text-muted-foreground">
                {m.shop_tier_spend_note()}
            </p>

            <Dialog
                open={editing !== null}
                onOpenChange={(o) => !o && setEditing(null)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {editing === 'new' || editing === null
                                ? m.shop_tier_add()
                                : m.shop_tier_edit_title({
                                      name: editing.name,
                                  })}
                        </DialogTitle>
                    </DialogHeader>
                    <FormProvider {...form}>
                        <form
                            onSubmit={submit}
                            className="space-y-4"
                            noValidate
                        >
                            <Field
                                name="name"
                                label={`${m.shop_tier_name_label()} *`}
                            >
                                <Input
                                    id="name"
                                    maxLength={SHOP_TIER_NAME_MAX}
                                    aria-invalid={!!errors.name}
                                    autoFocus
                                    {...form.register('name')}
                                />
                            </Field>
                            <Field
                                name="minSpend"
                                label={`${m.shop_tier_min_spend_input_label()} *`}
                            >
                                <Input
                                    id="minSpend"
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    step={1000}
                                    aria-invalid={!!errors.minSpend}
                                    aria-describedby="tier-min-spend-preview"
                                    {...form.register('minSpend')}
                                />
                                <MoneyPreview
                                    id="tier-min-spend-preview"
                                    value={minSpend}
                                />
                            </Field>
                            <Field
                                name="discount"
                                label={m.shop_tier_discount_input_label({
                                    max: String(SHOP_TIER_DISCOUNT_MAX),
                                })}
                            >
                                <Input
                                    id="discount"
                                    type="number"
                                    inputMode="decimal"
                                    min={0}
                                    max={SHOP_TIER_DISCOUNT_MAX}
                                    step={0.5}
                                    aria-invalid={!!errors.discount}
                                    {...form.register('discount')}
                                />
                            </Field>
                            <Controller
                                control={form.control}
                                name="freeShipping"
                                render={({ field }) => (
                                    <div className="flex items-center gap-2">
                                        <Switch
                                            id="tier-free-shipping"
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                        <Label htmlFor="tier-free-shipping">
                                            {m.shop_tier_free_shipping_input_label()}
                                        </Label>
                                    </div>
                                )}
                            />
                            {errors.root?.server && (
                                <p
                                    className="text-sm text-destructive"
                                    role="alert"
                                >
                                    {errors.root.server.message}
                                </p>
                            )}
                            <div className="flex justify-end gap-2 pt-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setEditing(null)}
                                >
                                    {m.common_cancel()}
                                </Button>
                                <Button type="submit" disabled={save.isPending}>
                                    {save.isPending
                                        ? m.common_saving()
                                        : m.common_save()}
                                </Button>
                            </div>
                        </form>
                    </FormProvider>
                </DialogContent>
            </Dialog>

            <Dialog
                open={deleting !== null}
                onOpenChange={(o) => !o && setDeleting(null)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.shop_tier_delete_title({
                                name: deleting?.name ?? '',
                            })}
                        </DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        {m.shop_tier_delete_desc()}
                    </p>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button
                            variant="outline"
                            onClick={() => setDeleting(null)}
                        >
                            {m.common_cancel()}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={confirmDelete}
                            disabled={remove.isPending}
                        >
                            {m.common_delete()}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    )
}
